import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import api from '../../utils/api';
import { getBrotherData } from '../../utils/auth';
import BrotherExport from './BrotherExport';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FilterContainer = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const Checkbox = styled.input`
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: #4a5568;
  cursor: pointer;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BrotherCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.subtle};
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Name = styled.h3`
  font-size: 1.25rem;
  color: #2d3748;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const Email = styled.p`
  color: #4a5568;
  margin: 0;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const Position = styled.p`
  color: ${({ theme }) => theme.colors.accent};
  font-weight: 500;
  margin: 0;
  font-size: 1rem;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: ${(props) => (props.$active ? '#C6F6D5' : '#FED7D7')};
  color: ${(props) => (props.$active ? '#276749' : '#9B2C2C')};

  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.2rem 0.4rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  background-color: ${({ theme }) => theme.colors.text};
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  transition: background-color 0.2s;
  flex: 1;

  &:hover {
    background-color: #2c5282;
  }

  &:disabled {
    background-color: #a0aec0;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    font-size: 0.875rem;
    padding: 0.4rem 0.8rem;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    font-size: 0.875rem;
    padding: 0.4rem 0.8rem;
  }
`;

const Message = styled.div`
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;

  ${(props) =>
    props.$type === 'success'
      ? `
      background-color: #c6f6d5;
      color: #276749;
      border: 1px solid #9ae6b4;
    `
      : `
      background-color: #fed7d7;
      color: #9b2c2c;
      border: 1px solid #feb2b2;
    `}

  @media (max-width: 768px) {
    font-size: 0.875rem;
    padding: 0.8rem;
  }
`;


const DisplayBrotherhood = () => {
  const [brothers, setBrothers] = useState([]);
  const [message, setMessage] = useState({ type: '', content: '' });
  const [editingBrother, setEditingBrother] = useState(null);
  const [showAllBrothers, setShowAllBrothers] = useState(false);
  const currentBrother = getBrotherData();
  const isPresident = currentBrother?.position === 'President';

  const positions = ['President', 'Rush Chair', 'Brother'];

  useEffect(() => {
    fetchBrothers();
  }, []);

  const fetchBrothers = async () => {
    try {
      const response = await api.get('/brothers');
      setBrothers(response.data.data);
    } catch (error) {
      setMessage({
        type: 'error',
        content: 'Failed to fetch brotherhood data'
      });
    }
  };

  const handleUpdatePosition = async (brotherId, newPosition) => {
    try {
      await api.patch(`/brothers/${brotherId}/position`, {
        position: newPosition
      });
      
      setMessage({
        type: 'success',
        content: 'Position updated successfully'
      });
      
      fetchBrothers();
      setEditingBrother(null);
    } catch (error) {
      setMessage({
        type: 'error',
        content: error.response?.data?.error || 'Failed to update position'
      });
    }
  };

  const handleToggleActive = async (brotherId, currentStatus) => {
    try {
      await api.patch(`/brothers/${brotherId}/toggle-active`);
      
      setMessage({
        type: 'success',
        content: `Brother ${currentStatus ? 'deactivated' : 'activated'} successfully`
      });
      
      fetchBrothers();
    } catch (error) {
      setMessage({
        type: 'error',
        content: error.response?.data?.error || 'Failed to update status'
      });
    }
  };

  const filteredBrothers = showAllBrothers 
    ? brothers 
    : brothers.filter(brother => brother.isActive);

  return (
    <Container>
      {message.content && (
        <Message $type={message.type}>
          {message.content}
        </Message>
      )}
      <FilterContainer>
        <BrotherExport brothers={filteredBrothers} />
        <Checkbox
          type="checkbox"
          id="showAll"
          checked={showAllBrothers}
          onChange={(e) => setShowAllBrothers(e.target.checked)}
        />
        <CheckboxLabel htmlFor="showAll">
          Show {showAllBrothers ? 'only active' : 'all'} brothers
        </CheckboxLabel>
      </FilterContainer>

      <Grid>
        {filteredBrothers.map(brother => (
          <BrotherCard key={brother._id}>
            <Name>{brother.name}</Name>
            <Email>{brother.email}</Email>
            <Position>{brother.position}</Position>
            <StatusBadge $active={brother.isActive}>
              {brother.isActive ? 'Active' : 'Inactive'}
            </StatusBadge>
            
            {isPresident && editingBrother === brother._id ? (
              <>
                <Select
                  defaultValue={brother.position}
                  onChange={(e) => handleUpdatePosition(brother._id, e.target.value)}
                >
                  {positions.map(position => (
                    <option key={position} value={position}>
                      {position}
                    </option>
                  ))}
                </Select>
                <Button onClick={() => setEditingBrother(null)}>
                  Cancel
                </Button>
              </>
            ) : isPresident && (
              <ButtonContainer>
                <Button onClick={() => setEditingBrother(brother._id)}>
                  Update Position
                </Button>
                <Button 
                  onClick={() => handleToggleActive(brother._id, brother.isActive)}
                  style={{
                    backgroundColor: brother.isActive ? '#E53E3E' : '#48BB78'
                  }}
                >
                  {brother.isActive ? 'Deactivate' : 'Activate'}
                </Button>
              </ButtonContainer>
            )}
          </BrotherCard>
        ))}
      </Grid>
    </Container>
  );
};

export default DisplayBrotherhood;